import type { AdConfig } from "../services/AdService";

export interface ReleaseEnv {
  [key: string]: string | boolean | undefined;
  VITE_ENABLE_ADSENSE?: string;
  VITE_MONETIZATION_COMPLIANCE_READY?: string;
  VITE_GOOGLE_CERTIFIED_CMP_READY?: string;
  VITE_ADSENSE_CLIENT?: string;
  VITE_ADSENSE_PUBLISHER_ID?: string;
  VITE_ADSENSE_MENU_SLOT?: string;
  VITE_ADSENSE_CAMPAIGN_SLOT?: string;
  VITE_ADSENSE_RESULTS_SLOT?: string;
  VITE_ENABLE_ADMOB?: string;
  VITE_ADMOB_TEST_MODE?: string;
  VITE_ADMOB_APP_ID_ANDROID?: string;
  VITE_ADMOB_BANNER_ANDROID?: string;
  VITE_ADMOB_REWARDED_CONTINUE_ANDROID?: string;
  VITE_ADMOB_REWARDED_DOUBLE_GOLD_ANDROID?: string;
}

export interface NativeAdMobConfig {
  enabled: boolean;
  testMode: boolean;
  appId?: string;
  bannerId?: string;
  rewardedContinueId?: string;
  rewardedDoubleGoldId?: string;
  readyForProduction: boolean;
}

const isTrue = (value: string | undefined): boolean => value === "true";
const isRealAdMobId = (value: string | undefined): boolean =>
  Boolean(value && value.startsWith("ca-app-pub-") && !value.includes("REPLACE") && !value.includes("XXXXX"));

export const buildWebAdConfig = (env: ReleaseEnv, nativePlatform: boolean): AdConfig => ({
  enabled: !nativePlatform && isTrue(env.VITE_ENABLE_ADSENSE),
  complianceReady: isTrue(env.VITE_MONETIZATION_COMPLIANCE_READY),
  certifiedCmpReady: isTrue(env.VITE_GOOGLE_CERTIFIED_CMP_READY),
  clientId: env.VITE_ADSENSE_CLIENT ?? env.VITE_ADSENSE_PUBLISHER_ID,
  menuSlot: env.VITE_ADSENSE_MENU_SLOT,
  campaignSlot: env.VITE_ADSENSE_CAMPAIGN_SLOT ?? env.VITE_ADSENSE_RESULTS_SLOT,
  admobEnabled: false,
});

export const buildNativeAdMobConfig = (env: ReleaseEnv): NativeAdMobConfig => {
  const enabled = isTrue(env.VITE_ENABLE_ADMOB);
  const testMode = env.VITE_ADMOB_TEST_MODE !== "false";
  const readyForProduction = Boolean(
    enabled
    && !testMode
    && isRealAdMobId(env.VITE_ADMOB_APP_ID_ANDROID)
    && isRealAdMobId(env.VITE_ADMOB_BANNER_ANDROID)
    && isRealAdMobId(env.VITE_ADMOB_REWARDED_CONTINUE_ANDROID)
    && isRealAdMobId(env.VITE_ADMOB_REWARDED_DOUBLE_GOLD_ANDROID),
  );

  return {
    enabled,
    testMode,
    appId: env.VITE_ADMOB_APP_ID_ANDROID,
    bannerId: env.VITE_ADMOB_BANNER_ANDROID,
    rewardedContinueId: env.VITE_ADMOB_REWARDED_CONTINUE_ANDROID,
    rewardedDoubleGoldId: env.VITE_ADMOB_REWARDED_DOUBLE_GOLD_ANDROID,
    readyForProduction,
  };
};

export const buildRuntimeAdConfig = (env: ReleaseEnv, nativePlatform: boolean): AdConfig => {
  const web = buildWebAdConfig(env, nativePlatform);
  const native = buildNativeAdMobConfig(env);
  return {
    ...web,
    admobEnabled: nativePlatform && native.enabled && (native.testMode || native.readyForProduction),
  };
};